import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateAashtoware, useGetAashtoware, useUpdateAashtoware } from '../../hooks/api/use-aashtoware.hook';
import { AashtowareApiModel } from '../../../../types/models/aashtoware.model';
import { LoadingIndicator } from '../LoadingIndicator';

import { AashtowareForm } from './AashtowareForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditAashtoware = () => {
  const navigate = useNavigate();
  const { aashtowareId } = useParams<{ aashtowareId: string }>();

  const {
    aashtoware,
    loading: loadingAashtoware,
    error: gettingError,
  } = useGetAashtoware<AashtowareApiModel>({ aashtowareId: Number(aashtowareId) });
  const [createAashtoware, { loading: creatingAashtoware, error: createError }] = useCreateAashtoware();
  const [updateAashtoware, { loading: updatingAashtoware, error: updateError }] = useUpdateAashtoware();

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<AashtowareForm>({ resolver: classValidatorResolver(AashtowareForm), values: aashtoware });

  const onSubmit: SubmitHandler<AashtowareForm> = async (submittedValues) => {
    const updatedValues = getDirtyValues(dirtyFields, submittedValues);
    if (!aashtoware) {
      await createAashtoware(submittedValues);
    } else {
      await updateAashtoware({ ...updatedValues, aashtowareId: aashtoware.id });
    }

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('code')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.code)}
          helperText={errors.code?.message}
          label="Code"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('specBook')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.specBook)}
          helperText={errors.specBook?.message}
          label="Spec Book"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('description')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('unit')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.unit)}
          helperText={errors.unit?.message}
          label="Unit"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('unitSystem')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAashtoware || updatingAashtoware}
          error={Boolean(errors.unitSystem)}
          helperText={errors.unitSystem?.message}
          label="Unit System"
          margin="dense"
          size="small"
          fullWidth
        />
      </>
    );
  };

  return (
    <Dialog open={true}>
      {loadingAashtoware ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${aashtoware ? 'Edit' : 'Add'} Aashtoware`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data.message || updateError?.response?.data.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {aashtoware ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
